import React from 'react';

// Country name → ISO 3166-1 alpha-2 code (lowercase, used by flagcdn.com).
// Keep this map extensible: new residence countries only need one entry here.
export const COUNTRY_CODE_MAP = {
  argentina: 'ar',
  brasil: 'br',
  brazil: 'br',
  chile: 'cl',
  ecuador: 'ec',
  españa: 'es',
  espana: 'es',
  spain: 'es',
  méxico: 'mx',
  mexico: 'mx',
  paraguay: 'py',
  usa: 'us',
  'estados unidos': 'us',
  'united states': 'us',
  uruguay: 'uy',
  colombia: 'co',
  perú: 'pe',
  peru: 'pe',
  venezuela: 've',
  bolivia: 'bo',
  panamá: 'pa',
  panama: 'pa',
  'costa rica': 'cr',
  'república dominicana': 'do',
  'republica dominicana': 'do',
  'rep dominicana': 'do',
  'rep. dominicana': 'do',
  cuba: 'cu',
  'puerto rico': 'pr',
  guatemala: 'gt',
  honduras: 'hn',
  nicaragua: 'ni',
  'el salvador': 'sv',
  portugal: 'pt',
  francia: 'fr',
  france: 'fr',
  italia: 'it',
  italy: 'it',
  alemania: 'de',
  germany: 'de',
  'reino unido': 'gb',
  uk: 'gb',
  'united kingdom': 'gb',
  canadá: 'ca',
  canada: 'ca',
  australia: 'au',
};

/** Devuelve el primer país canónico del string multi-valor (o null). */
export const primaryCountry = (paises) => {
  if (!paises) return null;
  const first = String(paises).split(',')[0]?.trim();
  return first || null;
};

/** Devuelve el código ISO2 (o null) para un nombre de país. Case-insensitive. */
export const countryCodeFor = (name) => {
  if (!name) return null;
  const key = String(name).trim().toLowerCase();
  return COUNTRY_CODE_MAP[key] || null;
};

/**
 * Bandera circular pequeña del país de residencia. Prioriza `pais_residencia`
 * (campo singular, canónico) y si no está, cae a `paises` (multi-valor, toma
 * el primero). Se muestra sólo si el país está en el mapa; si no, no renderiza
 * nada silenciosamente.
 *
 * Props:
 *  - talento: talento object (usa talento.pais_residencia y/o talento.paises)
 *  - paises / pais_residencia: overrides directos si no pasás el talento entero
 *  - size: 'xs' | 'sm' | 'md'  (default 'sm')
 *  - className: para posicionamiento absoluto en la card
 */
const SIZE_CLASSES = {
  xs: 'w-4 h-4',
  sm: 'w-5 h-5 md:w-6 md:h-6',
  md: 'w-7 h-7 md:w-8 md:h-8',
};

const CountryFlag = ({ talento, paises, pais_residencia, size = 'sm', className = '' }) => {
  // Priority: explicit pais_residencia prop > talento.pais_residencia >
  // explicit paises prop > talento.paises (first of the list).
  const residencia = pais_residencia ?? talento?.pais_residencia;
  const paisesRaw = paises ?? talento?.paises;
  const country = (residencia && String(residencia).trim()) || primaryCountry(paisesRaw);
  const code = countryCodeFor(country);
  if (!code) return null;
  const dim = SIZE_CLASSES[size] || SIZE_CLASSES.sm;

  return (
    <div
      className={`${dim} rounded-full overflow-hidden ring-2 ring-white shadow-md bg-white flex-shrink-0 ${className}`}
      title={country}
      data-testid={`country-flag-${code}`}
    >
      <img
        src={`https://flagcdn.com/w40/${code}.png`}
        srcSet={`https://flagcdn.com/w80/${code}.png 2x`}
        alt={country || code}
        loading="lazy"
        className="w-full h-full object-cover"
      />
    </div>
  );
};

export default CountryFlag;
